import { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { Settings, Server, Bell, KeyRound } from "lucide-react";

function EnumEditor({ label, values, onSave }) {
  const [text, setText] = useState((values || []).join(", "));
  useEffect(() => setText((values || []).join(", ")), [values]);
  return (
    <div className="flex items-center gap-3">
      <div className="w-32 text-sm font-semibold text-[#1F2733] shrink-0">{label}</div>
      <input value={text} onChange={(e) => setText(e.target.value)} className="flex-1 px-4 py-2 rounded-xl border border-[#DCE2EA] text-sm" />
      <button onClick={() => onSave(text.split(",").map((s) => s.trim()).filter(Boolean))} className="bg-[#F4F6FA] hover:bg-[#DCE2EA] border border-[#DCE2EA] rounded-full px-4 py-1.5 text-sm font-medium">Save</button>
    </div>
  );
}

export default function AdminConfig() {
  const [cfg, setCfg] = useState(null);
  const [health, setHealth] = useState(null);
  const [pin, setPin] = useState("");
  const [accessLog, setAccessLog] = useState([]);

  const load = () => {
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/system-health").then((r) => setHealth(r.data)).catch(() => {});
    api.get("/access-pin").then((r) => setPin(r.data.pin)).catch(() => {});
    api.get("/access-log").then((r) => setAccessLog(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const savePin = async () => {
    try { await api.put("/access-pin", { pin }); toast.success("Access code updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const save = async (key, val) => {
    try { await api.patch("/config", { [key]: val }); toast.success("Configuration saved"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!cfg) return <div className="text-[#5A687C]">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1F2733]">System Configuration</h1>
        <p className="text-[#5A687C] mt-1">Manage taxonomy, integrations, and notification settings.</p>
      </div>

      <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 space-y-4">
        <h2 className="font-bold text-[#1F2733] flex items-center gap-2"><Settings className="h-5 w-5 text-[#BA0C2F]" /> Taxonomy &amp; enums</h2>
        <EnumEditor label="Phases" values={cfg.phases} onSave={(v) => save("phases", v)} />
        <EnumEditor label="Statuses" values={cfg.statuses} onSave={(v) => save("statuses", v)} />
        <EnumEditor label="Priorities" values={cfg.priorities} onSave={(v) => save("priorities", v)} />
        <EnumEditor label="Task statuses" values={cfg.task_statuses} onSave={(v) => save("task_statuses", v)} />
        <EnumEditor label="RAID types" values={cfg.raid_types} onSave={(v) => save("raid_types", v)} />
      </div>

      <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6" data-testid="access-pin-card">
        <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-1"><KeyRound className="h-5 w-5 text-[#BA0C2F]" /> Portal access code</h2>
        <p className="text-xs text-[#5A687C] mb-4">The 4-digit code users enter on the home screen to open the portal.</p>
        <div className="flex items-center gap-3">
          <input data-testid="access-pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" maxLength={4} className="w-32 text-center text-2xl tracking-[0.4em] font-bold py-2.5 rounded-xl border border-[#DCE2EA]" />
          <button data-testid="save-access-pin-btn" onClick={savePin} className="bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full px-5 py-2.5 font-semibold text-sm">Update code</button>
        </div>
      </div>

      <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6" data-testid="access-log-card">
        <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-3"><KeyRound className="h-5 w-5 text-[#BA0C2F]" /> Portal access log</h2>
        <div className="max-h-64 overflow-y-auto divide-y divide-[#DCE2EA]/60">
          {accessLog.length === 0 && <div className="text-sm text-[#5A687C]">No access recorded yet.</div>}
          {accessLog.map((a) => (
            <div key={a.id} className="flex items-center gap-3 py-2 text-sm">
              <span className={`h-2 w-2 rounded-full shrink-0 ${a.success ? "bg-[#2E8B57]" : "bg-[#BA0C2F]"}`} />
              <span className="font-semibold text-[#1F2733]">{a.user_name}</span>
              <span className="text-[#98A2AF] text-xs">{a.role}</span>
              <span className="ml-auto text-xs text-[#98A2AF]">{new Date(a.timestamp).toLocaleString()}</span>
              <span className={`text-xs font-semibold ${a.success ? "text-[#2E8B57]" : "text-[#BA0C2F]"}`}>{a.success ? "entered" : "failed"}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-4"><Server className="h-5 w-5 text-[#BA0C2F]" /> Integrations &amp; health</h2>
          <div className="space-y-2">
            {Object.entries(cfg.integrations || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between p-3 rounded-xl bg-[#F4F6FA]">
                <span className="text-sm font-medium text-[#1F2733] capitalize">{k.replace(/_/g, " ")}</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1A5633]"><span className="h-2 w-2 rounded-full bg-[#2E8B57]" /> {v}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#98A2AF] mt-3">SSO/SAML and API key management placeholders — ready for future configuration.</p>
        </div>

        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-4"><Bell className="h-5 w-5 text-[#BA0C2F]" /> Notifications</h2>
          <div className="space-y-2">
            {["email", "slack", "teams"].map((ch) => (
              <label key={ch} className="flex items-center justify-between p-3 rounded-xl bg-[#F4F6FA] cursor-pointer">
                <span className="text-sm font-medium text-[#1F2733] capitalize">{ch}</span>
                <input type="checkbox" checked={!!cfg.notifications?.[ch]} onChange={(e) => save("notifications", { ...cfg.notifications, [ch]: e.target.checked })} />
              </label>
            ))}
          </div>
          {health && (
            <div className="mt-4 border-t border-[#DCE2EA] pt-4">
              <div className="text-xs font-bold text-[#5A687C] uppercase mb-2">Data volume</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(health.data_volume).map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="text-[#5A687C] capitalize">{k.replace(/_/g, " ")}</span><span className="font-bold text-[#1F2733]">{v}</span></div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
